import { FormEvent, useState } from 'react';
import apiEndpoints from '@repo/api-endpoints';
import { NewsletterSubscribeFormData } from './contracts';
import { Link } from 'react-router';
import { useTenantConfigQuery } from '@data/tenants';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { DeviceType } from '@repo/types';
import { useIsNewsletterEnabled } from './useIsNewsletterEnabled';

export type NewsletterSubscribeProps = NewsletterSubscribeFormData;

interface EmailFieldProps {
  hasSubmitted: boolean;
  error: string | null;
}

function EmailField({ hasSubmitted, error }: EmailFieldProps) {
  return (
    <>
      <label htmlFor="email" className="sr-only">
        Email address
      </label>
      <input
        id="email"
        type="email"
        name={'email'}
        placeholder="Enter email address"
        required
        autoComplete="email"
        aria-required="true"
        aria-invalid={hasSubmitted ? !!error : undefined}
        aria-describedby={error ? 'email-error' : undefined}
      />
    </>
  );
}

interface TermsFieldProps {
  id: string;
  name?: string;
}

function TermsField({ id, name }: TermsFieldProps) {
  return (
    <div className={'newsletter-form__terms-wrapper'}>
      <input type={'checkbox'} id={id} name={name} required aria-required="true" />
      <label className={'newsletter-form__terms-label'} htmlFor={id}>
        I agree to the{' '}
        <Link to="/data-protection" target="_blank">
          terms and conditions
        </Link>{' '}
        and{' '}
        <Link to="/privacy-policy" target="_blank">
          privacy policy
        </Link>
      </label>
    </div>
  );
}

function FormError({ error }: { error: string | null }) {
  if (!error) return null;

  return (
    <p className="newsletter-form__error" role="alert" id="email-error">
      {error}
    </p>
  );
}

function SuccessMessage() {
  return (
    <div className={'newsletter-form--success'}>
      <p>
        You have received an email with confirmation for opting in to our newsletter.
        <br />
        Please check your inbox and click on the link in order to confirm your subscription.
      </p>
    </div>
  );
}

interface FormViewProps {
  title?: string;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  hasSubmitted: boolean;
  error: string | null;
}

function MobileFormView({ title, onSubmit, hasSubmitted, error }: FormViewProps) {
  return (
    <form className={'newsletter-form'} onSubmit={onSubmit}>
      {title && <h3 className={'newsletter-form__title'}>{title}</h3>}
      <EmailField hasSubmitted={hasSubmitted} error={error} />

      <TermsField id={'terms'} />

      <button type="submit">Subscribe</button>
      <FormError error={error} />
    </form>
  );
}

function DesktopFormView({ title, onSubmit, hasSubmitted, error }: FormViewProps) {
  return (
    <form className={'newsletter-form'} onSubmit={onSubmit}>
      {title && <h3 className={'newsletter-form__title'}>{title}</h3>}

      <div className={'newsletter-form__input-wrapper'}>
        <EmailField hasSubmitted={hasSubmitted} error={error} />
        <button type="submit">Subscribe</button>
      </div>

      <TermsField id={'terms123'} name="terms123" />
      <FormError error={error} />
    </form>
  );
}

function NewsletterSubscribe({ title }: NewsletterSubscribeProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceType.mobile;
  const isNewsletterEnabled = useIsNewsletterEnabled();

  const { data: tenantConfig } = useTenantConfigQuery();
  const apiUrl = tenantConfig?.env?.apiUrl ?? '';
  const tenantId = tenantConfig?.tenantId ?? '';

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!apiUrl || !tenantId) {
      return;
    }

    // @ts-expect-error TODO: Implement proper type
    const email = e.currentTarget.elements.email.value;

    setHasSubmitted(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}${apiEndpoints.endpoints.newsletterSubscribe}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error(`Subscription request failed with status ${response.status}`);
      }
      setIsSubmitted(true);
    } catch (error) {
      setError('Failed to subscribe. Please try again.');
      console.error('Error subscribing to newsletter:', error);
    }
  };

  // Covers every placement at once: a CMS-booked component, the article page and the footer.
  if (!isNewsletterEnabled) {
    return null;
  }

  if (isSubmitted) {
    return <SuccessMessage />;
  }

  if (isMobile) {
    return <MobileFormView title={title} onSubmit={onSubmit} hasSubmitted={hasSubmitted} error={error} />;
  }

  return <DesktopFormView title={title} onSubmit={onSubmit} hasSubmitted={hasSubmitted} error={error} />;
}

export default NewsletterSubscribe;
